import { add, setHours, setMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { CurrentPlan } from '../../types/data';
import { generateEnhancedPlanDescription, generatePlanSummary } from '../plans';
import { getHistory } from '../storage/history';

function calculateEventDuration(type: string): number {
  switch (type) {
    case 'peptide':
      return 10; // 10 minutes
    case 'food':
      return 30; // 30 minutes
    case 'activity':
      return 60; // 1 hour
    default:
      return 10; // Default duration
  }
}

function parseFrequencyInterval(interval: string): { value: number; unit: string } {
  const match = interval.match(/(\d+)\s*(minute|day|week|month|hour|year)s?/i);
  if (!match) throw new Error(`Invalid frequency interval: ${interval}`);
  return { value: parseInt(match[1], 10), unit: match[2] };
}

function parseDurationPeriod(period: string): { value: number; unit: string } {
  const match = period.match(/(\d+)\s*(minute|day|week|month|hour|year)s?/i);
  if (!match) throw new Error(`Invalid duration period: ${period}`);
  return { value: parseInt(match[1], 10), unit: match[2] };
}

const timingMap = {
  AM: '08:00',
  PM: '20:00',
  'AM before exercise': '06:00',
  midday: '12:00',
  'AM/PM': '12:00',
  'N/A': '12:00',
  'before bed': '22:00',
  anytime: '12:00',
};

export interface CalendarOptions {
  alerts?: {
    before: number;  // minutes before event
    repeat?: number; // number of times to repeat
    interval?: number; // minutes between repeats
  }[];
  maxDuration?: number; // maximum duration in days, defaults to 1 year
}

export function generateCalendarEvents(plans: CurrentPlan[], options: CalendarOptions = {}): string {
  if (plans.length === 0) {
    throw new Error("No plans provided to generate calendar events.");
  }

  // Find the earliest start date across all enabled plans
  const startDate = plans.reduce((earliest, plan) => {
    if (!plan.start_date) return earliest;
    const planStartDate = new Date(plan.start_date);
    return planStartDate < earliest ? planStartDate : earliest;
  }, new Date());

  let calendar = "BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Custom Calendar//EN";
  const start = startDate;
  const maxEndDate = add(start, { days: options.maxDuration || 365 });
  const history = getHistory();

  plans.forEach((plan) => {
    if (!plan.enabled) return;
    if (!plan.start_date) return;

    const { value: frequencyValue, unit: frequencyUnit } = parseFrequencyInterval(plan.frequency_interval);
    const { value: durationValue, unit: durationUnit } = parseDurationPeriod(plan.duration_period);
    const durationInDays = durationUnit === "week" ? durationValue * 7 : durationValue;
    const frequencyInDays = frequencyUnit === "day" ? frequencyValue : frequencyValue * 7;

    const daysOn = plan.days_on || durationInDays;
    const daysOff = plan.days_off || 0;
    const repeatPeriod = plan.duration_repeat ? parseDurationPeriod(plan.duration_repeat).value : 0;

    let currentDate = start;
    let cycleEndDate = add(start, { days: daysOn });
    let planEndDate = add(start, { days: durationInDays });

    while (currentDate <= maxEndDate) {
      if (currentDate > planEndDate) break;

      // Generate events for the "on" days
      for (let i = 0; i < daysOn; i++) {
        if (currentDate > planEndDate || currentDate > maxEndDate) break;

        const timingTime = timingMap[plan.timing] || "12:00";
        const [hour, minute] = timingTime.split(":").map(Number);


        // Get local timezone
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Format event times
        const eventStart = formatInTimeZone(
          setHours(setMinutes(currentDate, minute), hour),
          localTimeZone,
          "yyyyMMdd'T'HHmmss"
        );

        const eventEnd = formatInTimeZone(
          add(setHours(setMinutes(currentDate, minute), hour), { minutes: calculateEventDuration(plan.type) }),
          localTimeZone,
          "yyyyMMdd'T'HHmmss"
        );

        const { description, injectionDetails } = generateEnhancedPlanDescription(plan, history);

        let eventDescription = '';

        if (injectionDetails) {
          eventDescription = `Injection Instructions:\\n${injectionDetails}\\n\\nPlan Details:\\n${description}`;
        } else {
          eventDescription = `${description}`;
        }

        const summary = generatePlanSummary(plan);

        // Start the event
        calendar += [
          '\nBEGIN:VEVENT',
          `\nSUMMARY:${summary}`,
          `\nDTSTART:${eventStart}`,
          `\nDTEND:${eventEnd}`,
          `\nDESCRIPTION:${eventDescription}`,
        ].join('');

        // Add alerts if specified
        if (options.alerts) {
          options.alerts.forEach(alert => {
            if (!alert.repeat) {
              calendar += `\nBEGIN:VALARM`;
              calendar += `\nACTION:DISPLAY`;
              calendar += `\nDESCRIPTION:Reminder`;
              calendar += `\nTRIGGER:-PT${alert.before}M`;
              calendar += `\nEND:VALARM`;
            } else {
              calendar += `\nBEGIN:VALARM`;
              calendar += `\nACTION:DISPLAY`;
              calendar += `\nDESCRIPTION:Reminder`;
              calendar += `\nTRIGGER:-PT${alert.before}M`;
              calendar += `\nREPEAT:${alert.repeat}`;
              calendar += `\nDURATION:PT${alert.interval || alert.before}M`;
              calendar += `\nEND:VALARM`;
            }
          });
        }

        calendar += `\nEND:VEVENT`;

        currentDate = add(currentDate, { days: frequencyInDays });
      }

      // Skip "off" days
      currentDate = add(cycleEndDate, { days: daysOff });

      // Update cycle end date for the next cycle
      cycleEndDate = add(currentDate, { days: daysOn });

      // Stop if no repeat
      if (repeatPeriod === 0) break;

      planEndDate = add(planEndDate, { days: repeatPeriod });
    }
  });

  calendar += "\nEND:VCALENDAR";
  return calendar;
}
